import { recordEventWithCheck } from "src/apiService/modules/events";
import type {
  EventVintedPayloadDrift,
  EventVintedSyncAborted,
  EventVintedSyncFinished,
  EventVintedSyncTriggered,
  VintedPayloadDriftIssue,
  VintedSyncAbortReason,
  VintedSyncImportType,
  VintedSyncJob,
} from "src/interfaces/event";
import type { VintedIntegration } from "src/interfaces/plaidIntegration.interface";

export {
  createVintedRefreshRunId,
  recordVintedSessionRefreshAborted,
  recordVintedSessionRefreshSucceeded,
  recordVintedSessionRefreshTriggered,
} from "src/utils/extension/vintedSessionRefreshEvents";

type VintedSyncEventFields = Omit<
  EventVintedSyncTriggered,
  "type" | "platform"
> & {
  job: VintedSyncJob;
  integrationId: string;
  syncRunId: string;
};

export function createVintedSyncRunId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function baseFromIntegration(
  integration: VintedIntegration,
  fields: Partial<VintedSyncEventFields> & {
    job: VintedSyncJob;
    syncRunId: string;
  }
): VintedSyncEventFields {
  const vintedUserId =
    fields.vintedUserId ??
    (integration.uid != null ? String(integration.uid) : undefined);
  const username = fields.username ?? integration.username;

  return {
    job: fields.job,
    syncRunId: fields.syncRunId,
    integrationId: fields.integrationId ?? integration.id,
    inventoryEnabled:
      fields.inventoryEnabled ??
      (integration.inventory !== false ? true : false),
    lastSync: fields.lastSync ?? integration.lastSync ?? null,
    lastSyncDateSales:
      fields.lastSyncDateSales ?? integration.vintedLastSyncDateSales ?? null,
    lastSyncDateInventory:
      fields.lastSyncDateInventory ??
      integration.vintedLastSyncDateInventory ??
      null,
    lastSyncDateTransactions:
      fields.lastSyncDateTransactions ??
      integration.vintedLastSyncDateTransactions ??
      null,
    ...(fields.trigger != null ? { trigger: fields.trigger } : {}),
    ...(fields.importType != null ? { importType: fields.importType } : {}),
    ...(username != null ? { username } : {}),
    ...(vintedUserId != null ? { vintedUserId } : {}),
    ...(fields.start != null ? { start: fields.start } : {}),
    ...(fields.end != null ? { end: fields.end } : {}),
    ...(fields.resumed != null ? { resumed: fields.resumed } : {}),
    ...(fields.message != null ? { message: fields.message } : {}),
  };
}

function safeRecord(
  event:
    | EventVintedSyncTriggered
    | EventVintedSyncFinished
    | EventVintedSyncAborted
    | EventVintedPayloadDrift
) {
  void recordEventWithCheck(event).catch((err) => {
    console.warn("[Vinted] Failed to record sync event", {
      type: event.type,
      error: (err as Error)?.message,
    });
  });
}

export function recordVintedSyncTriggered(
  integration: VintedIntegration,
  fields: Partial<VintedSyncEventFields> & {
    job: VintedSyncJob;
    syncRunId: string;
    message?: string;
  }
) {
  const base = baseFromIntegration(integration, fields);
  safeRecord({
    type: "vinted-sync-triggered",
    platform: "vinted",
    ...base,
    message:
      fields.message ??
      defaultTriggeredMessage(
        base.job,
        base.trigger,
        base.importType,
        base.resumed
      ),
  });
}

export function recordVintedSyncFinished(
  integration: VintedIntegration,
  fields: Partial<VintedSyncEventFields> & {
    job: VintedSyncJob;
    syncRunId: string;
    durationMs?: number;
    message?: string;
  }
) {
  const base = baseFromIntegration(integration, fields);
  safeRecord({
    type: "vinted-sync-finished",
    platform: "vinted",
    ...base,
    ...(fields.durationMs != null ? { durationMs: fields.durationMs } : {}),
    message:
      fields.message ??
      `Vinted ${base.job} finished${
        base.importType ? ` (${base.importType})` : ""
      }${base.resumed ? " (resumed)" : ""}`,
  });
}

export function recordVintedSyncAborted(
  integration: VintedIntegration | { id: string },
  fields: Partial<VintedSyncEventFields> & {
    job: VintedSyncJob;
    syncRunId: string;
    reason: VintedSyncAbortReason;
    error?: unknown;
    message?: string;
  }
) {
  const err = fields.error as Error | undefined;
  const stub: VintedIntegration =
    "username" in integration
      ? (integration as VintedIntegration)
      : ({ id: integration.id } as VintedIntegration);
  const base = baseFromIntegration(stub, fields);
  safeRecord({
    type: "vinted-sync-aborted",
    platform: "vinted",
    ...base,
    reason: fields.reason,
    ...(err?.name != null ? { errorName: err.name } : {}),
    ...(err?.stack != null ? { errorStack: err.stack } : {}),
    message:
      fields.message ??
      err?.message ??
      `Vinted ${base.job} aborted (${fields.reason})`,
  });
}

export function recordVintedPayloadDrift(
  fields: Omit<EventVintedPayloadDrift, "type" | "platform">
) {
  safeRecord({
    type: "vinted-payload-drift",
    platform: "vinted",
    ...fields,
    message:
      fields.message ?? defaultDriftMessage(fields.endpoint, fields.issue),
  });
}

function defaultDriftMessage(endpoint: string, issue: VintedPayloadDriftIssue) {
  switch (issue) {
    case "missing-lines":
      return `Vinted ${endpoint} response has no recognizable lines`;
    case "unknown-pagination":
      return `Vinted ${endpoint} pagination carries no page count`;
    case "page-cap-reached":
      return `Vinted ${endpoint} hit the page cap before an empty page`;
  }
}

function defaultTriggeredMessage(
  job: VintedSyncJob,
  trigger?: "auto" | "manual",
  importType?: VintedSyncImportType,
  resumed?: boolean
) {
  if (job === "daily-sync") {
    return `Vinted daily sync triggered (${trigger ?? "unknown"})`;
  }
  return `Vinted ${job} triggered${importType ? ` (${importType})` : ""}${
    resumed ? " (resumed)" : ""
  }`;
}
